import { useState, useRef, useCallback, useEffect } from 'react'
import { parseCsv } from '../../utils/csvParser'
import { EXPECTED_HEADERS, validateRow } from '../../utils/contactValidation'
import { bulkCreateContacts } from '../../api/contacts'

const TEMPLATE_HEADERS = 'name,phone,email,address,street,ward,district,province,tags,notes'
const TEMPLATE_EXAMPLE = 'Nguyen Van A,0901234567,example@email.com,123 Main St,123 Main St,Ward 1,District 1,Ho Chi Minh City,"friend;vip",First time customer'
const MAX_ROWS = 1000

// ─── top-level sub-components ────────────────────────────────────────────────

function DropZone({ onFile, parseError }) {
  const inputRef = useRef(null)
  const [dragging, setDragging] = useState(false)

  function handleDrop(e) {
    e.preventDefault()
    setDragging(false)
    const file = e.dataTransfer.files[0]
    if (file) onFile(file)
  }

  function handleChange(e) {
    const file = e.target.files[0]
    if (file) onFile(file)
    e.target.value = ''
  }

  return (
    <div
      className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer select-none transition-colors ${
        dragging
          ? 'border-[#1535c8] bg-[#e3eaf8]'
          : 'border-gray-200 bg-gray-50 hover:border-[#1535c8] hover:bg-[#f5f7ff]'
      }`}
      onDragOver={e => { e.preventDefault(); setDragging(true) }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".csv"
        className="hidden"
        onChange={handleChange}
      />
      <div className="text-3xl mb-2">📄</div>
      <p className="text-sm font-semibold text-gray-700">Drag &amp; drop a CSV file here</p>
      <p className="text-xs text-gray-400 mt-1">
        or <span className="text-[#1535c8] underline">browse files</span> — .csv only
      </p>
      {parseError && (
        <p className="mt-3 text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{parseError}</p>
      )}
    </div>
  )
}

function StageSelector({ value, onChange }) {
  return (
    <div className="flex gap-6">
      {[{ v: 'follower', label: 'Follower' }, { v: 'customer', label: 'Customer' }].map(({ v, label }) => (
        <label key={v} className="flex items-center gap-2 cursor-pointer">
          <input
            type="radio"
            name="import-stage"
            value={v}
            checked={value === v}
            onChange={() => onChange(v)}
            className="accent-[#1535c8]"
          />
          <span className="text-sm text-gray-700">{label}</span>
        </label>
      ))}
    </div>
  )
}

function RowStatus({ valid, hasWarning }) {
  if (!valid)       return <span className="text-red-500 font-bold">✗</span>
  if (hasWarning)   return <span className="text-amber-500 font-bold">⚠</span>
  return              <span className="text-green-500 font-bold">✓</span>
}

function PreviewTable({ rows }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-gray-100">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-gray-50 text-gray-500 text-left">
            <th className="px-2 py-2 w-10">#</th>
            <th className="px-2 py-2 w-8"></th>
            <th className="px-2 py-2">Name</th>
            <th className="px-2 py-2">Phone</th>
            <th className="px-2 py-2">Email</th>
            <th className="px-2 py-2">Issues</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr
              key={r.rowNum}
              className={`border-t border-gray-50 ${!r.valid ? 'bg-red-50' : r.hasWarning ? 'bg-amber-50' : ''}`}
            >
              <td className="px-2 py-1.5 text-gray-400">{r.rowNum}</td>
              <td className="px-2 py-1.5"><RowStatus valid={r.valid} hasWarning={r.hasWarning} /></td>
              <td className="px-2 py-1.5 font-medium text-gray-800 max-w-[120px] truncate">{r.normalized.name || <span className="text-gray-300">—</span>}</td>
              <td className="px-2 py-1.5 text-gray-600">{r.normalized.phone || <span className="text-gray-300">—</span>}</td>
              <td className="px-2 py-1.5 text-gray-600 max-w-[140px] truncate">{r.normalized.email || <span className="text-gray-300">—</span>}</td>
              <td className="px-2 py-1.5 text-red-500">
                {[...r.errors, ...r.warnings].join(' · ') || null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function ErrorList({ errors }) {
  const [expanded, setExpanded] = useState(false)
  if (!errors || errors.length === 0) return null
  const shown = expanded ? errors : errors.slice(0, 3)
  return (
    <div className="mt-3 text-xs">
      <button
        type="button"
        className="text-gray-500 underline mb-1"
        onClick={() => setExpanded(v => !v)}
      >
        {expanded ? 'Hide details' : `Show ${errors.length} error detail${errors.length > 1 ? 's' : ''}`}
      </button>
      {expanded && (
        <ul className="space-y-1 mt-1">
          {shown.map((e, i) => (
            <li key={i} className="text-red-600 bg-red-50 rounded px-2 py-1">
              {e.index != null ? `Row ${e.index + 1}: ` : ''}{e.reason || e.message || JSON.stringify(e)}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ─── main modal ──────────────────────────────────────────────────────────────

export default function ImportContactsModal({ open, onClose, tenantId, onImported, defaultStage = 'follower' }) {
  const [step, setStep]             = useState(1)
  const [stage, setStage]           = useState(defaultStage)
  const [parseError, setParseError] = useState(null)
  const [validatedRows, setValidatedRows] = useState([])
  const [importing, setImporting]   = useState(false)
  const [importResults, setImportResults] = useState(null)
  const requiredHeaders = stage === 'customer' ? ['name', 'phone'] : ['name']

  // Reset when opened
  useEffect(() => {
    if (open) {
      setStep(1)
      setStage(defaultStage)
      setParseError(null)
      setValidatedRows([])
      setImporting(false)
      setImportResults(null)
    }
  }, [open, defaultStage])

  // ESC to close
  useEffect(() => {
    if (!open) return
    function onKey(e) { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const handleFile = useCallback(async (file) => {
    setParseError(null)
    try {
      const text = await file.text()
      const { headers, rows } = parseCsv(text)

      const missingRequiredHeaders = requiredHeaders.filter(h => !headers.includes(h))
      if (missingRequiredHeaders.length > 0) {
        throw new Error(`Missing required column(s): ${missingRequiredHeaders.join(', ')}`)
      }

      const unknownHeaders = headers.filter(h => !EXPECTED_HEADERS.includes(h))
      if (unknownHeaders.length > 0) {
        throw new Error(`Unsupported column(s): ${unknownHeaders.join(', ')}. Allowed columns: ${EXPECTED_HEADERS.join(', ')}`)
      }

      const validated = []
      rows.forEach((row, i) => {
        const result = validateRow(row, validated.map(r => r.normalized), { stage })
        validated.push({ rowNum: i + 2, ...result })
      })
      setValidatedRows(validated)
      setStep(2)
    } catch (err) {
      setParseError(err.message)
    }
  }, [requiredHeaders, stage])

  const handleImport = useCallback(async () => {
    const importable = validatedRows.filter(r => r.valid || r.hasWarning)
    const capped = importable.slice(0, MAX_ROWS)
    const contacts = capped.map(r => ({ ...r.normalized, stage }))

    setImporting(true)
    try {
      const result = await bulkCreateContacts(tenantId, contacts)
      setImportResults(result)
      setStep(3)
    } catch (err) {
      const msg = err.response?.data?.message || 'Import failed. Please try again.'
      setImportResults({ error: msg })
      setStep(3)
    } finally {
      setImporting(false)
    }
  }, [validatedRows, stage, tenantId])

  function downloadTemplate() {
    const csv = TEMPLATE_HEADERS + '\n' + TEMPLATE_EXAMPLE
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'contacts_template.csv'
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  if (!open) return null

  // Derived counts for step 2
  const validRows    = validatedRows.filter(r => r.valid && !r.hasWarning)
  const warnRows     = validatedRows.filter(r => r.valid && r.hasWarning)
  const errorRows    = validatedRows.filter(r => !r.valid)
  const importable   = validRows.length + warnRows.length
  const overLimit    = importable > MAX_ROWS

  const stepTitle = step === 1 ? 'Import Contacts' : step === 2 ? 'Preview' : 'Import Results'

  return (
    <div
      className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="bg-white rounded-2xl w-full max-w-2xl shadow-xl flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <span className="font-bold text-gray-900 text-base">{stepTitle}</span>
            <span className="text-xs text-gray-400">Step {step} of 3</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 w-7 h-7 flex items-center justify-center text-xl rounded-full hover:bg-gray-100 transition-colors"
          >
            &times;
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">

          {/* ── Step 1: Upload ── */}
          {step === 1 && (
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Upload a CSV file to import contacts in bulk.</p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    Required columns: <span className="font-mono">{requiredHeaders.join(', ')}</span>. Optional: {stage === 'customer' ? 'email, address, street, ward, district, province, tags, notes' : 'phone, email, address, street, ward, district, province, tags, notes'}.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={downloadTemplate}
                  className="flex-shrink-0 text-xs font-semibold text-[#1535c8] border border-[#1535c8] rounded-lg px-3 py-1.5 hover:bg-[#e3eaf8] transition-colors"
                >
                  ↓ Download template
                </button>
              </div>

              <DropZone onFile={handleFile} parseError={parseError} />

              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Import as</p>
                <StageSelector value={stage} onChange={setStage} />
              </div>
            </div>
          )}

          {/* ── Step 2: Preview ── */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="flex gap-4 text-sm">
                <span className="flex items-center gap-1.5">
                  <span className="text-green-500 font-bold">✓</span>
                  <span className="font-semibold">{validRows.length}</span>
                  <span className="text-gray-500">valid</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="text-amber-500 font-bold">⚠</span>
                  <span className="font-semibold">{warnRows.length}</span>
                  <span className="text-gray-500">warnings</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="text-red-500 font-bold">✗</span>
                  <span className="font-semibold">{errorRows.length}</span>
                  <span className="text-gray-500">errors (will be skipped)</span>
                </span>
              </div>

              {overLimit && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  ⚠ File has {importable} importable rows — only the first {MAX_ROWS} will be sent.
                </div>
              )}

              <PreviewTable rows={validatedRows} />
            </div>
          )}

          {/* ── Step 3: Results ── */}
          {step === 3 && importResults && (
            <div className="space-y-4">
              {importResults.error ? (
                <div className="text-sm text-red-600 bg-red-50 rounded-xl px-4 py-3">{importResults.error}</div>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { label: 'Created',           value: importResults.created ?? 0,  color: 'text-green-600' },
                      { label: 'Skipped (dupes)',   value: importResults.skipped ?? 0,  color: 'text-amber-600' },
                      { label: 'Errors',            value: (importResults.errors?.length ?? importResults.failed ?? 0), color: 'text-red-600' },
                    ].map(({ label, value, color }) => (
                      <div key={label} className="bg-gray-50 rounded-xl p-3 text-center">
                        <div className={`text-2xl font-bold ${color}`}>{value}</div>
                        <div className="text-xs text-gray-500 mt-0.5">{label}</div>
                      </div>
                    ))}
                  </div>
                  <ErrorList errors={importResults.errors} />
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-gray-100 flex-shrink-0 gap-3">
          {step === 1 && (
            <button
              type="button"
              onClick={onClose}
              className="text-sm text-gray-500 hover:text-gray-700 font-semibold"
            >
              Cancel
            </button>
          )}

          {step === 2 && (
            <>
              <button
                type="button"
                onClick={() => setStep(1)}
                className="text-sm font-semibold border border-gray-200 text-gray-600 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors"
              >
                ← Back
              </button>
              <button
                type="button"
                onClick={handleImport}
                disabled={importing || importable === 0}
                className="ml-auto text-sm font-semibold bg-[#1535c8] text-white px-5 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#1228a8] transition-colors"
              >
                {importing ? 'Importing…' : `Import ${Math.min(importable, MAX_ROWS)} row${importable !== 1 ? 's' : ''}`}
              </button>
            </>
          )}

          {step === 3 && (
            <button
              type="button"
              onClick={() => { onImported && onImported(importResults); onClose() }}
              className="ml-auto text-sm font-semibold bg-[#1535c8] text-white px-5 py-2 rounded-lg hover:bg-[#1228a8] transition-colors"
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
